/**
 * StudentCopyCamera (web) — la photo d'une page de la copie, dans un navigateur, sur le design system
 * (`<ActivityCopyCamera>`) : la caméra du moteur (`useCopyCamera`), le viseur posé ici, et la photo
 * gardée rendue à l'activité (`onPhoto`).
 *
 * Elle remplace, pour l'élève du nouvel espace, l'ancien écran `camera` (`CameraComponent.web.js`), qui
 * reste celui de l'ancien espace. Montée le temps d'une prise : la fermer (la croix, « Valider »,
 * Échap) la démonte, et le moteur rend la caméra.
 *
 * En live, caméra allumée, elle reprend la piste de l'appel comme l'ancien (`useLiveTrack`) : le tuteur
 * continue de voir l'élève, et le navigateur n'ouvre pas la même caméra deux fois. La piste est lue à
 * l'ouverture : une caméra coupée pendant la prise ne change pas de source en route.
 *
 * La caméra qui ne s'ouvre pas (refusée, occupée par une autre application, absente) laisse importer
 * une image à la place : elle est rendue comme une photo gardée, marquée `import`.
 */
import { useMaybeRoomContext } from "@livekit/components-react"
import * as Device from "expo-device"
import { Track } from "livekit-client"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { View } from "react-native"

import { ActivityCopyCamera } from "../../../../../../design-system/student"
import { useCopyCamera, type LiveCameraTrack } from "../../../../../../engine/activity/copy/useCopyCamera"

export type CopyPhotoOrigin = "camera" | "import"

export type StudentCopyCameraProps = {
    /** L'activité est dans le live : la caméra de l'appel, si elle est allumée, sert de viseur. */
    inLive: boolean
    /** La photo gardée (« Valider ») ou importée, en adresse de données. */
    onPhoto: (uri: string, origin: CopyPhotoOrigin) => void
    onClose: () => void
}

const DEVICE_TYPE = { current: Device.deviceType, phone: Device.DeviceType.PHONE, tablet: Device.DeviceType.TABLET }

export function StudentCopyCamera(props: StudentCopyCameraProps) {
    const { inLive, onPhoto, onClose } = props
    const room = useMaybeRoomContext()
    const [liveTrack] = useState<LiveCameraTrack | null>(() => {
        const participant = room?.localParticipant
        if (!inLive || participant == null || !participant.isCameraEnabled) return null
        return (participant.getTrackPublication(Track.Source.Camera)?.track as LiveCameraTrack | undefined) ?? null
    })
    const camera = useCopyCamera({ open: true, liveTrack, deviceType: DEVICE_TYPE })
    const { state, setVideo } = camera
    // L'import rend la main après le sélecteur et la lecture de l'image : l'écran a pu partir entre-temps
    // (fin du live, autre activité), et sa page n'aurait plus d'activité où aller.
    const mounted = useRef(true)
    useEffect(
        () => () => {
            mounted.current = false
        },
        []
    )

    // Le viseur : un élément `<video>` dans la vue que la page lui donne (react-native-web la rend en `div`).
    // La page est dans un portail, montée après cet écran : la vue se lit quand elle arrive (`setHost`).
    const [host, setHost] = useState<HTMLElement | null>(null)
    const holder = useCallback((node: View | null) => setHost(node as unknown as HTMLElement | null), [])
    useEffect(() => {
        if (host == null || typeof document === "undefined") return
        const video = document.createElement("video")
        video.autoplay = true
        video.muted = true
        video.playsInline = true
        video.style.width = "100%"
        video.style.height = "100%"
        video.style.objectFit = "cover"
        host.appendChild(video)
        setVideo(video)
        return () => {
            setVideo(null)
            video.remove()
        }
    }, [host, setVideo])

    return (
        <ActivityCopyCamera
            visible
            phase={state.status === "closed" ? "starting" : state.status}
            viewfinder={<View ref={holder} style={{ width: "100%", height: "100%" }} />}
            photo={state.photo}
            failure={state.failure}
            onShutter={camera.shutter}
            onRetake={camera.retake}
            onConfirm={() => {
                if (state.photo != null) onPhoto(state.photo, "camera")
                onClose()
            }}
            onClose={onClose}
            onRetry={camera.retry}
            onImport={() => {
                camera.importPhoto().then((photo) => {
                    if (photo == null || !mounted.current) return
                    onPhoto(photo, "import")
                    onClose()
                })
            }}
            importFailed={camera.importFailed}
            onFlip={camera.canFlip ? camera.flip : undefined}
        />
    )
}
